import type RouterService from "@ember/routing/router-service";
import type Transition from "@ember/routing/transition";
import Service, { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import { version1IsLowerThanVersion2 } from "parog-web/utils/version";

interface Payload {
  page?: string;
  from?: string;
  to?: string;
}
interface Message {
  type: string;
  payload?: Payload;
}

interface Permissions {
  canCaptureSteps?: boolean;
  canCaptureVisits?: boolean;
}
/**
 * A service for communicating with the native mobile application.
 *
 * It follows the Web Worker conventions with:
 *
 * - a postMessage method used by the Web app to send a message to the mobile app,
 * - a onMessage method, used by the native app to send a message to the Web app.
 *
 * Messages are JSON serialized in strings.
 * They are expected to contain a type and a payload.
 * The message is then interpreted depending on the type.
 */
export default class NativeAppService extends Service {
  @service declare router: RouterService;

  minimumVersions = {
    android: "1.11.0",
    ios: "1.0"
  };

  declare platform: "ios" | "android";
  declare version: string;

  androidStoreUrl =
    "https://play.google.com/store/apps/details?id=io.semeia.mentalwise&hl=fr";
  iosStoreUrl = "https://apps.apple.com/fr/app/mentalwise/id1539637294";

  @tracked
  permissions: Permissions = {};

  setPlatform(): void {
    this._extractInformationFromMobileUserAgent();
    this.postMessage({ type: "app-is-ready" });
  }

  /**
   * @returns true if the Web app is running in the context of the native app.
   */
  get enabled(): boolean {
    const nativeAppBridgeMounted = Boolean(
      window.android || window.webkit?.messageHandlers?.nativeApp
    );
    return this.platform != undefined && nativeAppBridgeMounted;
  }

  setup(): void {
    // The native app will access the service through this global attribute.
    window.nativeAppService = this;

    this.setPlatform();

    if (this.enabled) {
      this.router.on("routeDidChange", this._postRouteDidChange.bind(this));
    }
  }

  /**
   * Send a message to the native application.
   */
  postMessage(message: Message): void {
    if (!this.enabled) return;

    switch (this.platform) {
      case "ios":
        window.webkit?.messageHandlers?.nativeApp.postMessage(
          JSON.stringify(message)
        );
        break;
      case "android":
        window.android.postMessage(JSON.stringify(message));
        break;
      // default:

      // console.debug("Platform is not set. Cannot post a message.");
    }
  }

  /**
   * Called by the native to send a message to the Web.
   */
  onMessage(message: string): void {
    const deserialized = JSON.parse(message);
    switch (deserialized.type) {
      case "permission-status-retrieved":
        this._permissionStatusRetrieved(deserialized.payload);
        break;
      default:
        console.error(`Unkown message type sent to the Web app: ${message}`);
    }
  }

  showPermissionPage(): void {
    this.postMessage({
      type: "show-page",
      payload: {
        page: "onboarding"
      }
    });
  }

  patientLoggedIn(): void {
    this.postMessage({ type: "patient-logged" });
  }

  _postRouteDidChange({ from, to }: Transition): void {
    if (!to) return;

    const payload: Payload = {
      to: to.name
    };

    /**
     * in PR https://github.com/Semeia-io/Platform/pull/8163, the login routes changed
     * But the ios native app has the legacy route "patient-web.login" hardcoded
     * and we cannot update all the native apps on the patient's devices.
     *
     * We handle this special case here to make sure the ios app will properly
     * render the native login view when the ember app transitions to the new
     * login "login.index" route.
     */
    const transitionningToLoginOnIos =
      this.platform == "ios" && to.name == "login.index";
    if (transitionningToLoginOnIos) payload.to = "patient-web.login";

    if (from) payload["from"] = from.name;

    this.postMessage({
      type: "route-did-change",
      payload
    });
  }

  get shouldShowUpdateMessage(): boolean {
    return version1IsLowerThanVersion2(
      this.version,
      this.minimumVersions[this.platform]
    );
  }

  get urlToStore(): string | null {
    switch (this.platform) {
      case "android":
        return this.androidStoreUrl;
      case "ios":
        return this.iosStoreUrl;
      default:
        return null;
    }
  }

  get runningOnIos(): boolean {
    return (
      window.navigator.userAgent.includes("iPad") ||
      window.navigator.userAgent.includes("iPhone")
    );
  }

  get runningOnAndroid(): boolean {
    return window.navigator.userAgent.includes("Android");
  }

  get runningOnNativeApp(): boolean {
    return window.navigator.userAgent.includes("Wise");
  }

  _permissionStatusRetrieved(permissions: Permissions): void {
    this.permissions = permissions;
  }

  _extractInformationFromMobileUserAgent(): void {
    const version = window.navigator.userAgent.split("/")?.[1]?.split(" ")?.[0];
    if (window.navigator.userAgent.includes("(Android)")) {
      console.debug("Android user agent detected");
      this.platform = "android";
      this.version = version;
    } else if (window.navigator.userAgent.includes("(iOS)")) {
      console.debug("iOS user agent detected");
      this.platform = "ios";
      this.version = version;
    }
  }
}
